import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/**
 * Filters drawer — a modal side panel that opens from the inline-start (left)
 * edge at every viewport width. Keeps id="filters-drawer" and exposes
 * open()/close()/toggle() so the existing bindings and facets.js keep working.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 * @extends {Component<Refs>}
 */
export class FiltersDrawer extends Component {
  requiredRefs = ['panel'];

  /** @type {boolean} */
  #isClosing = false;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.isOpen) {
      const { panel } = this.refs;
      if (!panel.open) panel.showModal();
      lockScroll(panel);
      this.#addEventListeners();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#removeEventListeners();
  }

  open() {
    if (this.isOpen || this.#isClosing) return;

    const { panel } = this.refs;

    panel.classList.add('theme-drawer__dialog--opening');
    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    lockScroll(panel);
    panel.showModal();

    this.#focusInitialElement();
    onAnimationEnd(panel, () => panel.classList.remove('theme-drawer__dialog--opening'), { subtree: false });

    this.setAttribute('open', '');
    this.#addEventListeners();
  }

  async close() {
    if (!this.isOpen || this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();

    const { panel } = this.refs;
    this.removeAttribute('open');
    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove('theme-drawer__dialog--opening');
      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.#restoreFocus();

    this.#isClosing = false;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  #addEventListeners() {
    document.addEventListener('keydown', this.#onKeyDown);
    this.refs.panel.addEventListener('click', this.#onBackdropClick);
  }

  #removeEventListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
    this.refs.panel.removeEventListener('click', this.#onBackdropClick);
  }

  #onBackdropClick = (event) => {
    const { panel } = this.refs;
    if (panel.querySelector('dialog[open]')) return;
    if (isClickedOutside(event, panel)) this.close();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;

    event.preventDefault();
    this.close();
  };

  #focusInitialElement() {
    /** @type {HTMLElement | null} */ (this.refs.panel.querySelector('.theme-drawer__close-button'))?.focus();
  }

  #restoreFocus() {
    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }
  }
}

if (!customElements.get('filters-drawer')) {
  customElements.define('filters-drawer', FiltersDrawer);
}